import { useCallback, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconBuilder } from '@components/icons/builder';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { Question, QuestionAnswer } from 'api/ai-builder/requests';

import { AiBuilderQuestionCard, OTHER_OPTION_LABEL } from './components/ai-builder-question-card';

type AiBuilderQuestionsProps = {
  questions: Question[];
  onSubmit: (answers: QuestionAnswer[]) => void;
};

const EMPTY_ANSWER: QuestionAnswer = { selectedOptions: [], customText: '' };

export function AiBuilderQuestions({ questions, onSubmit }: AiBuilderQuestionsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, QuestionAnswer>>({});
  const { t } = useScopedTranslate(locale);

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentIndex] ?? EMPTY_ANSWER;
  const isLastQuestion = currentIndex === questions.length - 1;
  const isFirstQuestion = currentIndex === 0;

  const canProceed = useCallback(() => {
    const answer = answers[currentIndex] ?? EMPTY_ANSWER;
    const hasSelection = answer.selectedOptions.length > 0;
    const isOtherValid =
      !answer.selectedOptions.includes(OTHER_OPTION_LABEL) || answer.customText.trim().length > 0;
    return hasSelection && isOtherValid;
  }, [answers, currentIndex]);

  const handleNext = useCallback(() => {
    if (canProceed() && !isLastQuestion) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [canProceed, isLastQuestion, currentIndex]);

  const handlePrevious = useCallback(() => {
    if (!isFirstQuestion) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [isFirstQuestion, currentIndex]);

  const handleSubmit = useCallback(() => {
    if (canProceed() && isLastQuestion) {
      const allAnswers = questions.map((_, index) => answers[index] ?? EMPTY_ANSWER);
      onSubmit(allAnswers);
    }
  }, [canProceed, isLastQuestion, answers, questions, onSubmit]);

  const handleAnswerChange = useCallback(
    (answer: QuestionAnswer) => {
      setAnswers((prev) => ({ ...prev, [currentIndex]: answer }));
    },
    [currentIndex],
  );

  const isValid = canProceed();

  return (
    <QuestionsCard>
      <QuestionsHeader>
        <Stack orientation="horizontal" space={2} align="center">
          <IconBuilder color="elementPurple80" size={16} />
          <Text font="semibold14" text={t('clarifyingQuestions')} color="textHelper" />
        </Stack>
      </QuestionsHeader>

      <AiBuilderQuestionCard
        question={currentQuestion}
        questionIndex={currentIndex}
        totalQuestions={questions.length}
        answer={currentAnswer}
        onAnswerChange={handleAnswerChange}
      />

      <ButtonRow>
        <Button
          variant="ghost"
          onClick={handlePrevious}
          disabled={isFirstQuestion}
          text={t('previous')}
        />
        {isLastQuestion ? (
          <Button
            variant="primary-blue"
            onClick={handleSubmit}
            disabled={!isValid}
            text={t('confirm')}
          />
        ) : (
          <Button
            variant="primary-blue"
            onClick={handleNext}
            disabled={!isValid}
            text={t('next')}
          />
        )}
      </ButtonRow>
    </QuestionsCard>
  );
}

const QuestionsCard = styled.div({
  width: '100%',
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: pxToRem(16),
  background: theme.color.backgroundLightDark,
});

const QuestionsHeader = styled.div({
  marginBottom: pxToRem(12),
});

const ButtonRow = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
})({
  marginTop: pxToRem(16),
  paddingTop: pxToRem(12),
  borderTop: `1px solid ${theme.color.border10}`,
});

const locale = {
  en: {
    clarifyingQuestions: 'Clarifying Questions',
    previous: 'Previous',
    next: 'Next',
    confirm: 'Confirm',
  },
};
